import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import SuperPanelShell from '../../components/super/SuperPanelShell';
import './superPanel.css';

const emptyForm = {
  shopName: '',
  city: '',
  name: '',
  email: '',
  phone: '',
  password: '',
  isActive: true,
};

export default function ShopAccountForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!isEdit) return undefined;

    let cancelled = false;

    async function loadAccount() {
      setFetching(true);
      try {
        const response = await authFetch(`${API_BASE}/v1/shop/accounts`);
        const result = await parseJsonResponse(response);
        const account = (result.data || []).find((item) => item.id === id);
        if (!account) throw new Error('Shop account not found');
        if (!cancelled) {
          setForm({
            shopName: account.shopName || '',
            city: account.city || '',
            name: account.name || '',
            email: account.email || '',
            phone: account.phone || '',
            password: account.password?.startsWith('(old hashed') ? '' : (account.password || ''),
            isActive: account.isActive !== false,
          });
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(getUserErrorMessage(error, 'Unable to load shop account.'));
          navigate('/super/shops');
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    }

    loadAccount();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        shopName: form.shopName.trim(),
        city: form.city.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        isActive: form.isActive,
      };
      if (form.password) payload.password = form.password;

      if (!isEdit && !form.password) {
        toast.error('Password is required for new shop accounts');
        setLoading(false);
        return;
      }

      const url = isEdit
        ? `${API_BASE}/v1/shop/accounts/${id}`
        : `${API_BASE}/v1/shop/accounts`;

      const response = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit ? payload : { ...payload, password: form.password }),
      });

      await parseJsonResponse(response);
      toast.success(isEdit ? 'Shop account updated' : 'Shop account created');
      navigate('/super/shops');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to save shop account.'));
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <SuperPanelShell title="Loading..." breadcrumb="Super Panel">
        <p className="sp-muted">Loading shop account...</p>
      </SuperPanelShell>
    );
  }

  return (
    <SuperPanelShell
      title={isEdit ? 'Edit Shop Login' : 'Create Shop Login'}
      breadcrumb={`Super Panel • ${isEdit ? 'Edit' : 'Create'} Shop`}
      showBack
    >
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: '#12121f', color: '#f5d76e', border: '1px solid rgba(212,175,55,0.3)' },
        }}
      />

      <form className="sp-form-card" onSubmit={handleSubmit} autoComplete="off">
        {[
          ['shopName', 'Shop Name', 'text'],
          ['city', 'City', 'text'],
          ['name', 'Contact Person', 'text'],
          ['email', 'Login Email', 'email'],
          ['phone', 'Phone Number', 'tel'],
        ].map(([field, label, type]) => (
          <div key={field} className="sp-form-group">
            <label className="sp-form-label" htmlFor={`shop-${field}`}>{label}</label>
            <input
              id={`shop-${field}`}
              type={type}
              className="sp-form-input"
              value={form[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              required
              autoComplete="off"
            />
          </div>
        ))}

        <div className="sp-form-group">
          <label className="sp-form-label" htmlFor="shop-password">
            Password {isEdit && '(shown as saved — change to update)'}
          </label>
          <input
            id="shop-password"
            type="text"
            className="sp-form-input"
            value={form.password}
            onChange={(e) => handleChange('password', e.target.value)}
            required={!isEdit}
            minLength={6}
            autoComplete="off"
          />
        </div>

        {isEdit && (
          <div className="sp-form-group">
            <label className="sp-form-label" htmlFor="shop-active">Account Status</label>
            <select
              id="shop-active"
              className="sp-form-input"
              value={form.isActive ? 'active' : 'disabled'}
              onChange={(e) => handleChange('isActive', e.target.value === 'active')}
            >
              <option value="active">Active — can log in to shop panel</option>
              <option value="disabled">Inactive — login blocked</option>
            </select>
          </div>
        )}

        <div className="sp-form-actions">
          <button type="button" className="sp-btn sp-btn--ghost" onClick={() => navigate('/super/shops')}>
            Cancel
          </button>
          <button type="submit" className="sp-btn sp-btn--gold" disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Update Shop' : 'Create Shop Login'}
          </button>
        </div>
      </form>
    </SuperPanelShell>
  );
}
